/**
 * Cornerstone project content config.
 * Facts only from authorized sources. Date-sensitive fields include review dates.
 */

export const project = {
  name: 'Cornerstone',
  builder: 'Primont',
  builderLegal: 'Primont Homes (Heritage Heights 1) Inc.',
  publisher: {
    name: 'Independent marketing registration site',
    role: 'Lead registration funnel for Cornerstone interest — not the builder',
  },
  city: 'Brampton',
  area: 'Northwest Brampton',
  locationLine: 'Master-planned community in Northwest Brampton',
  addressLine: '',
  phone: '+1 (289) 278-6786',
  phoneHref: 'tel:+12892786786',
  email: '',
  emailHref: '',
  officialUrl: 'https://primont.com/low-rise/brampton/cornerstone',
  domain: 'livingatcornerstone.ca',
  siteUrl: 'https://livingatcornerstone.ca',
  websiteSourceTag: 'livingatcornerstone.ca',
  productTypes: ['Townhomes', 'Detached Homes'],
  productNote: 'Future condominiums are part of the long-term community vision.',
  status: {
    label: 'Now Selling',
    detail: 'Now selling',
    publicPriceMessage: 'from $629,990',
    priceNote: 'Prices and availability change without notice.',
    sources: [
      'Primont public project page https://primont.com/low-rise/brampton/cornerstone (Now Selling · starting from $629,990, checked 2026-10-20)',
    ],
    lastVerified: '2026-10-20',
    reviewBy: '2026-11-03',
  },
  hero: {
    // Single page H1: keyword-focused. Must match the <h1> in index.html #static-geo.
    h1: 'New Townhomes & Detached Homes in Northwest Brampton',
    // Large display status line (styled, not a heading).
    headline: 'Now selling',
    support: '3–5 bedroom townhomes and detached homes. Now selling from $629,990.',
    primaryCta: 'Register now',
    secondaryCta: 'Watch video',
    artistNote: "Rendering is an artist's concept. Prices and availability change without notice.",
  },
  story: {
    title: 'A place to grow into the life you imagine',
    lead: 'New townhomes and detached homes. Now selling from $629,990.',
    answer:
      'Live at Cornerstone is a master-planned community of new townhomes and detached homes by Primont Homes in Northwest Brampton, now selling from $629,990. Cornerstone by Primont Homes is also searched as Primont Cornerstone. People also type conerstone or corner stone. Register on this site for VIP pricing, floor plans, and release updates. This is an independent registration site, not the builder corporate website.',
    paragraphs: [
      'Cornerstone is taking shape in Northwest Brampton as a master-planned community built around belonging — distinct districts connected by everyday places to gather, walk, and settle in.',
      'Whether you are beginning a chapter together or making room for a growing family, the neighbourhood is planned so each street feels personal while remaining part of a larger, welcoming whole.',
      'Townhomes, detached homes, and future condominiums are envisioned within one thoughtful community — a place shaped by connection, comfort, and the people who will call it home.',
    ],
    closing: 'Now selling. Register to stay connected as the community takes shape.',
  },
  highlights: [
    {
      title: 'Thoughtful exteriors',
      text: 'Architectural elevations with clay brick, stone detailing, and modern maintenance-free materials (as per plan).',
    },
    {
      title: 'Energy-minded openings',
      text: 'ENERGY STAR® qualified windows and doors with Low-E glass (as specified for townhomes and detached homes).',
    },
    {
      title: 'Multi-generational living',
      text: 'Select plans are designed for generations to live together, with a separate entrance where grading permits.',
    },
    {
      title: 'Premium kitchen standards',
      text: 'Purchaser selection of premium cabinetry with quartz countertops from the builder’s standard samples (townhomes).',
    },
  ],
  homes: [
    {
      id: 'townhomes',
      title: 'Townhomes',
      summary:
        'Freehold townhome collections including urban double-garage, traditional two-storey, and dual-front options. Detailed model pricing is available after registration.',
      cta: 'Request townhome pricing',
      downloadId: 'towns_pricelist',
    },
    {
      id: 'detached',
      title: 'Detached homes',
      summary:
        'Double-car garage detached homes on 33′ and 36′ frontages. Floor plans and VIP pricing are shared after you register.',
      cta: 'Request detached pricing',
      downloadId: 'detached_pricelist',
    },
  ],
  location: {
    title: 'Northwest Brampton',
    summary:
      'Cornerstone sits within Northwest Brampton’s growing master-planned fabric — positioned for new-home living with room to put down roots.',
    points: [
      'Master-planned community in Northwest Brampton',
      'Townhomes and detached homes, with future condominiums in the community vision',
      'Register below for VIP updates on releases, floor plans, and pricing',
    ],
    mapQuery: 'Northwest Brampton, ON',
    showMap: true,
  },
  video: {
    youtubeId: 'o0YzOQJFR38',
    youtubeUrl: 'https://www.youtube.com/watch?v=o0YzOQJFR38',
    title: 'Cornerstone community video',
    teaserSrc: '/assets/video/cornerstone-social-teaser.mp4',
    poster: '/assets/web/video-poster.webp',
  },
  gallery: [
    {
      src: '/assets/web/hero-render-1280.jpg',
      srcSet:
        '/assets/web/hero-render-640.jpg 640w, /assets/web/hero-render-960.jpg 960w, /assets/web/hero-render-1280.jpg 1280w, /assets/web/hero-render-1600.jpg 1600w',
      webpSrcSet:
        '/assets/web/hero-render-640.webp 640w, /assets/web/hero-render-960.webp 960w, /assets/web/hero-render-1280.webp 1280w, /assets/web/hero-render-1600.webp 1600w',
      alt: 'Cornerstone townhomes — artist’s concept with family lifestyle',
      width: 1600,
      height: 688,
      fit: 'cover',
    },
    {
      src: '/assets/web/Cornerstone_Renderings-p1-1400.jpg',
      srcSet:
        '/assets/web/Cornerstone_Renderings-p1-800.jpg 800w, /assets/web/Cornerstone_Renderings-p1-1400.jpg 1400w',
      alt: 'Cornerstone detached home elevation — artist’s concept',
      width: 1400,
      height: 668,
      fit: 'cover',
    },
    {
      src: '/assets/web/Cornerstone_Brochure_Digital-p5-1400.jpg',
      srcSet:
        '/assets/web/Cornerstone_Brochure_Digital-p5-800.jpg 800w, /assets/web/Cornerstone_Brochure_Digital-p5-1400.jpg 1400w',
      alt: 'Cornerstone District aerial community study — artist’s concept',
      width: 1400,
      height: 884,
      fit: 'contain',
      span: 'full',
    },
    {
      // Optimized from the 877 KB original (kept in the 2026-10-20 backup folder).
      src: '/assets/web/cornerstone-creative-1080.jpg',
      srcSet: '/assets/web/cornerstone-creative-800.jpg 800w, /assets/web/cornerstone-creative-1080.jpg 1080w',
      webpSrcSet: '/assets/web/cornerstone-creative-800.webp 800w, /assets/web/cornerstone-creative-1080.webp 1080w',
      alt: 'Cornerstone 3–5 bedroom townhomes and detached homes in Brampton — authorized Primont creative',
      width: 1080,
      height: 1080,
      fit: 'contain',
      span: 'full',
    },
  ],
  sitePlan: {
    preview: '/assets/web/Cornerstone_Siteplan-p1-1400.jpg',
    previewSrcSet:
      '/assets/web/Cornerstone_Siteplan-p1-800.jpg 800w, /assets/web/Cornerstone_Siteplan-p1-1400.jpg 1400w',
    alt: 'Cornerstone Phase 1 site plan preview',
    downloadId: 'siteplan',
    note: 'Lots and availability change. Some lots may already be marked sold on current plans.',
  },
  downloads: [
    {
      id: 'brochure',
      title: 'Digital brochure',
      type: 'PDF',
      access: 'gated',
      description: 'Community overview brochure.',
    },
    {
      id: 'floorplans',
      title: 'Floor plans',
      type: 'PDF',
      access: 'gated',
      description: 'Townhome and detached floor plans.',
    },
    {
      id: 'siteplan',
      title: 'Site plan',
      type: 'PDF',
      access: 'gated',
      description: 'Phase site plan.',
    },
    {
      id: 'features',
      title: 'Features & finishes',
      type: 'PDF',
      access: 'gated',
      description: 'Townhome and detached feature sheet.',
    },
    {
      id: 'renderings',
      title: 'Renderings package',
      type: 'PDF',
      access: 'gated',
      description: 'Authorized community renderings.',
    },
    {
      id: 'towns_pricelist',
      title: 'Townhome price list',
      type: 'PDF',
      access: 'gated',
      description: 'VIP townhome pricing. Last updated September 16, 2026.',
      lastUpdated: '2026-09-16',
      reviewBy: '2026-10-16',
    },
    {
      id: 'detached_pricelist',
      title: 'Detached price list',
      type: 'PDF',
      access: 'gated',
      description: 'VIP detached pricing. Last updated September 16, 2026.',
      lastUpdated: '2026-09-16',
      reviewBy: '2026-10-16',
    },
    {
      id: 'incentives',
      title: 'Exclusive incentives',
      type: 'PDF',
      access: 'gated',
      description: 'Limited-time incentive summary. Confirm current terms when you register.',
      lastUpdated: '2026-09-16',
      reviewBy: '2026-10-01',
    },
  ],
  excludedAssets: [
    'Worksheet',
    'Worksheet submission protocol',
    'Sample Agreement of Purchase and Sale',
  ],
  faq: [
    {
      q: 'What is Live at Cornerstone?',
      a: 'Live at Cornerstone is a master-planned community of new townhomes and detached homes by Primont Homes in Northwest Brampton, now selling from $629,990. Register on livingatcornerstone.ca for VIP pricing and floor plans.',
    },
    {
      q: 'What is Primont Cornerstone?',
      a: 'Primont Cornerstone is Cornerstone by Primont Homes, a master-planned community of new townhomes and detached homes in Northwest Brampton, now selling from $629,990. People also type it conerstone or corner stone. Register on livingatcornerstone.ca for VIP pricing and floor plans.',
    },
    {
      q: 'Where is Cornerstone?',
      a: "Cornerstone is in the future Heritage Heights neighbourhood of Northwest Brampton, on the Mississauga Road corridor near Halton Hills and Caledon (per primont.com). Heritage Heights is the City of Brampton's last large undeveloped area, planned under the OLT-approved Heritage Heights Secondary Plan (brampton.ca). The builder has not published an exact street address on its project page, so ask the sales office before you visit. Last verified 2026-10-20.",
      html: 'Cornerstone is in the future Heritage Heights neighbourhood of Northwest Brampton, on the Mississauga Road corridor near Halton Hills and Caledon (per <a href="https://primont.com/low-rise/brampton/cornerstone" rel="noopener noreferrer" target="_blank">primont.com</a>). Heritage Heights is the City of Brampton\'s last large undeveloped area, planned under the OLT-approved Heritage Heights Secondary Plan (<a href="https://www.brampton.ca/EN/Business/planning-development/Pages/Heritage-Heights.aspx" rel="noopener noreferrer" target="_blank">brampton.ca</a>). The builder has not published an exact street address on its project page, so ask the sales office before you visit. Last verified 2026-10-20.',
    },
    {
      q: 'How do you get around from Cornerstone?',
      a: 'The builder lists Highway 407, Highway 410, the future Highway 413 and Mount Pleasant GO Station on the Kitchener line. GO currently runs hourly two-way weekend trains between Mount Pleasant and Union Station. Metrolinx plans two-way, all-day 30-minute service to Mount Pleasant once Kitchener line expansion is complete. Highway 413 is in detail design and procurement (highway413.ca), with no confirmed opening date. Check gotransit.com for current schedules. Last verified 2026-10-20.',
      html: 'The builder lists Highway 407, Highway 410, the future Highway 413 and Mount Pleasant GO Station on the Kitchener line. GO currently runs hourly two-way weekend trains between Mount Pleasant and Union Station. Metrolinx plans two-way, all-day 30-minute service to Mount Pleasant once Kitchener line expansion is complete. Highway 413 is in detail design and procurement (<a href="https://www.highway413.ca/" rel="noopener noreferrer" target="_blank">highway413.ca</a>), with no confirmed opening date. Check <a href="https://www.gotransit.com/" rel="noopener noreferrer" target="_blank">gotransit.com</a> for current schedules. Last verified 2026-10-20.',
    },
    {
      q: 'Which schools serve Cornerstone?',
      a: 'New communities are assigned to schools as they open. Check the Peel District School Board and Dufferin-Peel Catholic District School Board school locators for the address on your Agreement of Purchase and Sale. The builder notes nearby daycares, Montessori programs, public schools and private academies.',
      html: 'New communities are assigned to schools as they open. Check the <a href="https://www.peelschools.org/school-finder" rel="noopener noreferrer" target="_blank">Peel District School Board</a> and <a href="https://www.dpcdsb.org/schools/school-locator" rel="noopener noreferrer" target="_blank">Dufferin-Peel Catholic District School Board</a> school locators for the address on your Agreement of Purchase and Sale. The builder notes nearby daycares, Montessori programs, public schools and private academies.',
    },
    {
      q: 'Is this the Cornerstone community in Calgary?',
      a: 'No. This site covers Cornerstone by Primont in Northwest Brampton, Ontario. It is not affiliated with Cornerstone communities in Calgary or Edmonton.',
    },
    {
      q: 'What home types are offered?',
      a: 'Townhomes and detached homes are part of the current offering. Future condominiums are described in the community vision.',
    },
    {
      q: 'Are there new townhomes in Brampton?',
      a: 'Yes. Cornerstone by Primont in Northwest Brampton offers new freehold townhomes, including 3 to 5 bedroom collections. Homes are now selling from $629,990. Register on this site for VIP pricing and floor plans.',
    },
    {
      q: 'Are there new homes in Brampton?',
      a: 'Yes. Cornerstone by Primont is a new-home community in Northwest Brampton with townhomes and detached homes, now selling from $629,990. Register for VIP updates on releases, pricing, and floor plans.',
    },
    {
      q: 'What is the starting price message?',
      a: 'Cornerstone is now selling, with public pricing starting from $629,990. Prices and availability change without notice. Exact model pricing is shared after you register on this page.',
    },
    {
      q: 'What deposit does Primont ask for at Cornerstone?',
      a: 'Primont Homes says a new home at Cornerstone can be purchased with a 5% deposit. Confirm the current deposit before you rely on it. This is an independent registration site, not the builder website. Last checked 2026-10-24.',
    },
    {
      q: 'Do any Cornerstone homes have a separate entrance?',
      a: 'Primont Homes says select Cornerstone homes include an in-law suite or a separate entrance. This is not every home. Register on this site and ask which plans include it. Last checked 2026-10-24.',
    },
    {
      q: 'How do I get pricing and floor plans?',
      a: 'Register on this page for VIP updates. After you submit, you can request gated resources such as price lists and floor plans. You can also call +1 (289) 278-6786.',
    },
    {
      q: 'How do I speak with someone about Cornerstone?',
      a: 'Register on this site for VIP updates. You can also call +1 (289) 278-6786 to reach the independent registration team.',
    },
    {
      q: 'Are renderings exact?',
      a: 'Illustrations and renderings are the artist’s interpretation. Prices, sizes, specifications, and availability are subject to change without notice.',
    },
  ],
  seo: {
    title: 'Cornerstone by Primont | Live at Cornerstone in Brampton',
    description:
      'Live at Cornerstone by Primont in Northwest Brampton. New townhomes and detached homes now selling from $629,990. Register for VIP pricing and floor plans.',
    keywords: [
      'Live at Cornerstone',
      'Cornerstone by Primont',
      'Cornerstone Brampton',
      'Cornerstone homes Brampton',
      'Cornerstone townhouse',
      'new townhomes in Brampton',
      'detached homes in Brampton',
      'new homes in Northwest Brampton',
      'Primont Cornerstone',
      'Primont Homes',
      'Primont Cornerstone registration',
    ],
    ogImage: '/assets/web/hero-1280.jpg',
  },
  legal: {
    disclaimer:
      'Illustrations and renderings are the artist’s interpretation. Prices, sizes, and specifications are subject to change without notice and are subject to availability. Additional fees, terms, and conditions may apply. Some incentives apply to select homes only. The developer reserves the right to change or remove an incentive at any time without notice. Homes are sold unfurnished unless stated otherwise. Brokers protected. E.&O.E.',
    marketingNote:
      'This is an independent marketing registration website for Cornerstone interest. It is not Primont’s corporate website. Reach us on the form on this page or by calling +1 (289) 278-6786.',
    representationNote:
      'We do not directly represent the builder. Register on this page for updates, or call +1 (289) 278-6786.',
    copyrightYear: 2026,
  },
  brand: {
    primary: '#D0E860',
    primaryDark: '#B8D14A',
    ink: '#2A241C',
    inkSoft: '#4A4338',
    paper: '#FAFAF6',
    paperAlt: '#F3F4EC',
    white: '#FFFFFF',
    black: '#111111',
    border: '#E2E4D8',
    focus: '#0B57D0',
    fontDisplay: '"Fraunces", "Times New Roman", serif',
    fontBody: '"DM Sans", "Helvetica Neue", sans-serif',
  },
  analytics: {
    gtmId: import.meta.env.VITE_GTM_ID || '',
    ga4Id: import.meta.env.VITE_GA4_ID || 'G-HTL4MZK974',
  },
};

export default project;
